<script>
import Icon from 'next/icon/Icon.vue';

const TOAST_VARIANTS = {
  success: {
    icon: 'i-lucide-circle-check',
    iconClass: 'text-n-teal-11 bg-n-teal-3',
    progressClass: 'bg-n-teal-9',
    titleClass: 'text-n-teal-11',
    title: 'Tudo certo',
  },
  positive: {
    icon: 'i-lucide-circle-check',
    iconClass: 'text-n-teal-11 bg-n-teal-3',
    progressClass: 'bg-n-teal-9',
    titleClass: 'text-n-teal-11',
    title: 'Tudo certo',
  },
  error: {
    icon: 'i-lucide-circle-alert',
    iconClass: 'text-n-ruby-11 bg-n-ruby-3',
    progressClass: 'bg-n-ruby-9',
    titleClass: 'text-n-ruby-11',
    title: 'Algo deu errado',
  },
  alert: {
    icon: 'i-lucide-circle-alert',
    iconClass: 'text-n-ruby-11 bg-n-ruby-3',
    progressClass: 'bg-n-ruby-9',
    titleClass: 'text-n-ruby-11',
    title: 'Algo deu errado',
  },
  warning: {
    icon: 'i-lucide-triangle-alert',
    iconClass: 'text-n-amber-11 bg-n-amber-3',
    progressClass: 'bg-n-amber-9',
    titleClass: 'text-n-amber-11',
    title: 'Atenção',
  },
  info: {
    icon: 'i-lucide-info',
    iconClass: 'text-n-blue-11 bg-n-blue-3',
    progressClass: 'bg-n-blue-9',
    titleClass: 'text-n-blue-11',
    title: 'Aviso',
  },
};

export default {
  components: { Icon },
  props: {
    message: { type: String, default: '' },
    action: {
      type: Object,
      default: () => {},
    },
    duration: {
      type: Number,
      default: 2500,
    },
    paused: {
      type: Boolean,
      default: false,
    },
    // Muda quando o mesmo alerta é atualizado (ex.: mais pedidos novos).
    revision: {
      type: Number,
      default: 0,
    },
  },
  emits: ['dismiss'],
  data() {
    return {
      dismissTimerId: null,
      dismissStartedAt: null,
      dismissRemaining: this.duration,
      isPaused: false,
    };
  },
  computed: {
    isActionPresent() {
      return this.action && this.action.message;
    },
    // Several actions in one alert: { label, onClick, to, primary, keepOpen }.
    buttons() {
      return Array.isArray(this.action?.buttons)
        ? this.action.buttons.filter(button => button?.label)
        : [];
    },
    // Sticky alerts wait for the person: no timer and no progress bar.
    isSticky() {
      return Boolean(this.action?.sticky);
    },
    variant() {
      return this.action?.variant || 'info';
    },
    toastStyle() {
      return TOAST_VARIANTS[this.variant] || TOAST_VARIANTS.info;
    },
    // O alerta pode trazer título próprio (ex.: "Novos pedidos"); senão, o do tipo.
    title() {
      return this.action?.title || this.toastStyle.title;
    },
    progressStyle() {
      return {
        '--toast-duration': `${this.duration}ms`,
        'animation-play-state': this.isPaused ? 'paused' : 'running',
      };
    },
  },
  watch: {
    revision() {
      this.dismissRemaining = this.duration;
      if (!this.isPaused) this.startDismissTimer();
    },
    paused(value) {
      if (value) {
        this.pauseDismiss();
      } else {
        this.resumeDismiss();
      }
    },
  },
  mounted() {
    this.startDismissTimer();
    if (this.paused) this.pauseDismiss();
  },
  beforeUnmount() {
    this.clearDismissTimer();
  },
  methods: {
    clearDismissTimer() {
      if (this.dismissTimerId) {
        window.clearTimeout(this.dismissTimerId);
        this.dismissTimerId = null;
      }
    },
    startDismissTimer() {
      this.clearDismissTimer();
      if (this.isSticky) return;
      if (!this.dismissRemaining || this.dismissRemaining <= 0) return;

      this.dismissStartedAt = Date.now();
      this.dismissTimerId = window.setTimeout(() => {
        this.dismissTimerId = null;
        this.$emit('dismiss');
      }, this.dismissRemaining);
    },
    pauseDismiss() {
      // Prazos reais (como desfazer uma exclusão) não podem congelar no hover.
      if (this.action?.pauseOnHover === false) return;
      if (this.isPaused) return;
      this.isPaused = true;

      if (!this.dismissTimerId) return;

      const elapsed = Date.now() - this.dismissStartedAt;
      this.dismissRemaining = Math.max(0, this.dismissRemaining - elapsed);
      this.clearDismissTimer();
    },
    runAction() {
      this.action?.onClick?.();
      this.$emit('dismiss');
    },
    runButton(button) {
      if (button.to) this.$router?.push(button.to);
      button.onClick?.();
      if (!button.keepOpen) this.$emit('dismiss');
    },
    resumeDismiss() {
      if (!this.isPaused) return;
      this.isPaused = false;
      this.startDismissTimer();
    },
  },
};
</script>

<template>
  <div
    class="snackbar-card group pointer-events-auto relative mb-3 flex w-full items-start gap-3 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-left shadow-[0_14px_36px_-14px_rgba(15,23,42,0.45)] dark:bg-n-solid-2"
    role="alert"
    @mouseenter="pauseDismiss"
    @mouseleave="resumeDismiss"
    @focusin="pauseDismiss"
    @focusout="resumeDismiss"
  >
    <div
      class="grid size-9 shrink-0 place-content-center rounded-lg"
      :class="toastStyle.iconClass"
    >
      <Icon :icon="toastStyle.icon" class="size-[1.125rem]" />
    </div>

    <div class="min-w-0 flex-1 pt-px">
      <p
        class="m-0 text-[11px] font-semibold uppercase leading-4 tracking-wide"
        :class="toastStyle.titleClass"
      >
        {{ title }}
      </p>
      <div class="mt-0.5 text-sm leading-5 text-n-slate-12">
        {{ message }}
      </div>
      <div v-if="isActionPresent" class="mt-2">
        <router-link
          v-if="action.type == 'link'"
          :to="action.to"
          class="text-sm font-semibold text-n-brand hover:underline"
        >
          {{ action.message }}
        </router-link>
        <div
          v-else-if="action.type == 'button'"
          class="flex flex-wrap items-center gap-2"
        >
          <button
            type="button"
            class="h-8 rounded-lg px-3 text-xs font-semibold text-n-slate-12 ring-1 ring-n-weak transition hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            @click="runAction"
          >
            {{ action.message }}
          </button>
          <span v-if="action.hint" class="text-xs text-n-slate-11">
            {{ action.hint }}
          </span>
        </div>
      </div>
      <div
        v-if="buttons.length"
        class="mt-2.5 flex flex-wrap items-center gap-2"
      >
        <button
          v-for="button in buttons"
          :key="button.label"
          type="button"
          class="h-8 rounded-lg px-3 text-xs font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :class="
            button.primary
              ? 'bg-n-brand text-white shadow-sm hover:brightness-110'
              : 'text-n-slate-12 ring-1 ring-n-weak hover:bg-n-alpha-2'
          "
          @click="runButton(button)"
        >
          {{ button.label }}
        </button>
      </div>
    </div>

    <button
      type="button"
      :aria-label="$t('GENERAL.CLOSE')"
      class="grid size-7 shrink-0 place-content-center rounded-md text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
      @click="$emit('dismiss')"
    >
      <Icon icon="i-lucide-x" class="size-4" />
    </button>

    <span
      v-if="!isSticky"
      :key="revision"
      class="toast-progress absolute bottom-0 left-0 h-0.5 opacity-70"
      :class="toastStyle.progressClass"
      :style="progressStyle"
    />
  </div>
</template>

<style scoped>
.toast-progress {
  animation: toast-progress-shrink var(--toast-duration) linear forwards;
}

@keyframes toast-progress-shrink {
  from {
    width: 100%;
  }

  to {
    width: 0%;
  }
}
</style>
